import React, { useState } from "react";
import {
  ProductAccessDialog,
  ProductNavBreadcrumb,
  ProductSwitcherPane,
} from "@merge-api/merge-javascript-shared";
import type { ProductAccessReason, ProductKey } from "@merge-api/merge-javascript-shared";
import type { ProductSwitcherState } from "./useProductSwitcher";

interface Props {
  switcher: ProductSwitcherState;
  /** Opens this product's support channel from the access dialog. */
  onContactSupport?: () => void;
}

interface BlockedSelection {
  product: ProductKey;
  reason: ProductAccessReason;
}

/**
 * The switcher's two states in the left nav: the breadcrumb naming the current
 * product, or the pane listing every product in place of the nav items. A
 * greyed product opens a dialog saying why it cannot be reached.
 */
const ProductSwitcherNav = ({ switcher, onContactSupport }: Props) => {
  const [blocked, setBlocked] = useState<BlockedSelection | null>(null);
  const { currentProduct, products, isPaneOpen, openPane, closePane, switchingTo, switchTo } =
    switcher;
  if (currentProduct == null) return null;

  const onSelect = (product: ProductKey) => {
    if (product === currentProduct) {
      closePane();
      return;
    }
    switchTo(product);
  };

  if (isPaneOpen) {
    return (
      <>
        <ProductSwitcherPane
          className="my-4"
          products={products}
          pendingProduct={switchingTo}
          onSelect={onSelect}
          onSelectUnavailable={(product, reason) => setBlocked({ product, reason })}
        />
        <ProductAccessDialog
          open={blocked !== null}
          product={blocked?.product ?? currentProduct}
          reason={blocked?.reason ?? "not_permitted"}
          onClose={() => setBlocked(null)}
          onContactSupport={onContactSupport}
        />
      </>
    );
  }
  return <ProductNavBreadcrumb className="mt-4" product={currentProduct} onClick={openPane} />;
};

export default ProductSwitcherNav;
